import { T, toGlobalID, toLocalID } from 'src/shared/helpers';
import { salesChannelsLabels } from '../../../products/utils';
import { generateStoreNumbers } from '../../../salesChannels/helpers';
import type { ProductToolData, RuleField, SidePanelProduct, ToolCategory, ToolDefinition } from './types';

export const TOOL_CATEGORY_LABELS: Record<ToolCategory, string> = {
    'product-setup': T._('Core Tools', 'Admin'),
    'orders-output': T._('Orders & Output', 'Admin'),
    ai: T._('AI Creative Tools', 'Admin'),
    'tools-settings': T._('Tools & Settings', 'Admin')
};

export const TOOL_DEFINITIONS: ToolDefinition[] = [
    // Product Setup
    {
        key: 'printing-methods',
        name: T._('Printing methods', 'Admin'),
        description: T._('Define print types, output and customization tools', 'Admin'),
        route: '/printing-methods',
        permissions: ['PRINTING_METHODS'],
        detailRoute: '/printing-method',
        buildEmbeddedUrl: (itemId, productId) =>
            itemId ? `/printing-method-embedded/${toLocalID(itemId)}?embedded=true` : `/printing-method/?embedded=true&fromProductId=${productId}`,
        addPanelKey: 'printingMethod',
        environments: ['CUSTOMIZER'],
        category: 'product-setup',
        helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360016836099-Printing-Methods',
        hiddenForPOD: true
    },
    {
        key: 'images',
        name: T._('Images and clipart galleries', 'Admin'),
        description: T._('Provide your customers with a gallery of artworks, cliparts, logos, and any other image to personalize your products.', 'Admin'),
        route: '/tools/image-gallery',
        permissions: ['IMAGES'],
        buildEmbeddedUrl: () => `/tools/image-gallery?embedded=true`,
        environments: ['CUSTOMIZER'],
        category: 'product-setup',
        helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360007632300-Images-and-Clipart-Gallery',
        isBoolean: true,
        fullModeOnlyInSidebar: true
    },
    {
        key: 'templates',
        name: T._('Pre-designed templates', 'Admin'),
        description: T._('Provide your customers with editable designs and set your own rules and restrictions for each design element.', 'Admin'),
        route: '/tools/template-product-selection/templates',
        permissions: ['CUSTOMIZER_PRE_DESIGNED_TEMPLATES'],
        buildEmbeddedUrl: (itemId, pid) =>
            `/tools/template-product-selection/templates/${toLocalID(pid)}?embedded=true${itemId === '_new' ? '&openNew=true' : ''}`,
        environments: ['CUSTOMIZER'],
        category: 'product-setup',
        helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360016320114-Pre-designed-Templates',
        fullModeOnlyInSidebar: true,
        dataKey: 'templates',
        displayMode: 'items'
    },
    {
        key: 'pricing',
        name: T._('Pricing rules', 'Admin'),
        description: T._('Markup prices for customizable products', 'Admin'),
        route: '/pricings-rules',
        permissions: ['PRICING_RULES'],
        buildEmbeddedUrl: (itemId, productId) =>
            itemId ? `/pricing-rule-embedded?pricing_id=${toLocalID(itemId)}&embedded=true` : `/pricing-rule-embedded?embedded=true&fromProductId=${productId}`,
        addPanelKey: 'pricingRule',
        environments: ['CUSTOMIZER', 'COMPOSER'],
        category: 'product-setup',
        helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360015984779-How-to-create-pricing-rules',
        customizerTool: true,
        isBoolean: true,
        isSingleRule: true,
        dataKey: 'pricingRules',
        displayMode: 'items-with-status'
    },
    {
        key: '3d-preview',
        name: T._('3D preview and AR', 'Admin'),
        description: T._('Real-time 3D view and Augmented Reality', 'Admin'),
        route: '/3Dpreview-and-AR/products',
        permissions: ['CUSTOMIZER_3D_PREVIEW'],
        buildSetupRoute: (pid) => `/3Dpreview-and-AR/?product_id=${pid}`,
        buildEmbeddedUrl: (_itemId, pid) => `/3Dpreview-and-AR/?product_id=${pid}&embedded=true`,
        environments: ['CUSTOMIZER'],
        category: 'product-setup',
        helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360011939619-3D-Preview-and-Augmented-Reality',
        isBoolean: true,
        fullModeOnly: true,
        isProductSpecific: true,
        dataKey: 'has3DScene',
        displayMode: 'boolean'
    },
    // Orders & Output (alphabetical)
    {
        key: 'placement',
        name: T._('Advanced Print File', 'Admin'),
        description: T._('Nested designs, QR codes, dynamic info', 'Admin'),
        route: '/tools/advanced-print-file/products',
        permissions: ['ADVANCED_PRINT_FILES'],
        buildSetupRoute: (pid) => `/tools/advanced-print-file/${pid}?openNew=true`,
        buildEmbeddedUrl: (itemId, pid) => {
            const modelID = toLocalID(pid) ?? pid;
            if (itemId) {
                const localItemId = toLocalID(itemId);
                return `/tools/advanced-print-file/${modelID}?placementId=${localItemId ?? itemId}&embedded=true`;
            }
            return `/tools/advanced-print-file/${modelID}?openNew=true&embedded=true`;
        },
        environments: ['CUSTOMIZER'],
        category: 'orders-output',
        helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/9686184040348-Advanced-Print-Files-Builder',
        hiddenForPOD: true,
        isProductSpecific: true,
        fullModeOnlyInSidebar: true,
        dataKey: 'placements'
    },
    {
        key: 'bulk-variations',
        name: T._('Bulk Variation Form', 'Admin'),
        description: T._('Multiple variants in one customization', 'Admin'),
        route: '/tools/bulk-variation-form/products',
        permissions: ['BULK_VARIATION_FORMS'],
        buildSetupRoute: (pid) => `/tools/bulk-variation-form/${toLocalID(pid)}`,
        buildEmbeddedUrl: (_itemId, pid) => `/tools/bulk-variation-form/${toLocalID(pid)}?embedded=true`,
        environments: ['CUSTOMIZER'],
        category: 'orders-output',
        helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360012930340-Bulk-Variation-Forms',
        isBoolean: true,
        isSingleRule: true,
        inlineCreate: true,
        fullModeOnlyInSidebar: true,
        dataKey: 'bulkVariationEnabled',
        displayMode: 'boolean'
    },
    {
        key: 'extra-options',
        name: T._('Extra Options', 'Admin'),
        description: T._('Additional attributes not shown on the product', 'Admin'),
        route: '/tools/extra-options',
        permissions: ['PRODUCTS_EDIT'],
        detailRoute: '/tools/extra-option',
        buildSetupRoute: () => '/tools/extra-option/',
        buildEmbeddedUrl: (itemId) => `/tools/extra-option/${itemId}?embedded=true`,
        addPanelKey: 'extraOption',
        environments: ['CUSTOMIZER'],
        category: 'orders-output',
        helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/25246484297116-Extra-Options',
        fullModeOnly: true,
        fullModeOnlyInSidebar: true,
        dataKey: 'extraOptions',
        displayMode: 'items'
    },
    {
        key: 'get-a-quote',
        name: T._('Get a quote', 'Admin'),
        description: T._('Add a quote request button to customizer', 'Admin'),
        route: '/tools/get-a-quote',
        permissions: ['QUOTES_SETTINGS'],
        detailRoute: '/tools/get-a-quote/quote-rule',
        buildSetupRoute: () => '/tools/get-a-quote/quote-rule/',
        addPanelKey: 'quoteRule',
        buildEmbeddedUrl: (itemId) => `/tools/get-a-quote/quote-rule/${toLocalID(itemId)}?embedded=true`,
        environments: ['CUSTOMIZER', 'COMPOSER'],
        category: 'orders-output',
        helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360015664999--Get-a-quote-tool',
        isBoolean: true,
        isSingleRule: true,
        inlineCreate: true,
        fullModeOnlyInSidebar: true,
        dataKey: 'quoteRule',
        displayMode: 'single-item'
    },
    {
        key: 'moq',
        name: T._('Minimum Order Quantity', 'Admin'),
        description: T._('Min quantity rules per product or variant', 'Admin'),
        route: '/tools/moq',
        permissions: ['MOQ'],
        detailRoute: '/tools/moq-rule',
        buildSetupRoute: () => '/tools/moq-rule/',
        addPanelKey: 'moqRule',
        buildEmbeddedUrl: (itemId) => `/tools/moq-rule/${toLocalID(itemId)}?embedded=true`,
        environments: ['CUSTOMIZER'],
        category: 'orders-output',
        helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/26249997300892-Minimum-Order-Quantity',
        isBoolean: true,
        isSingleRule: true,
        hiddenForPOD: true,
        inlineCreate: true,
        dedicatedEditor: true,
        dataKey: 'moqRules',
        displayMode: 'items'
    },

    // AI Creative Tools
    {
        key: 'ai-image-generation',
        name: T._('AI Image Generator & Editor', 'Admin'),
        description: T._('Generate and edit images with AI', 'Admin'),
        route: '/ai-creative-tools/image-generation-editing',
        permissions: ['AI_CREATIVE_TOOLS'],
        buildEmbeddedUrl: () => '/ai-creative-tools/image-generation-editing?embedded=true',
        environments: ['CUSTOMIZER'],
        category: 'ai',
        helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/23868482378524',
        isBoolean: true,
        inlineCreate: true,
        dedicatedEditor: true,
        dataKey: 'aiImageGenConfigured',
        displayMode: 'boolean'
    },
    {
        key: 'ai-design-generation',
        name: T._('AI Design Generator', 'Admin'),
        description: T._('Generate complete designs with AI', 'Admin'),
        route: '/ai-creative-tools/design-generation',
        permissions: ['AI_CREATIVE_TOOLS'],
        buildEmbeddedUrl: () => '/ai-creative-tools/design-generation?embedded=true',
        environments: ['CUSTOMIZER'],
        category: 'ai',
        helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/24190036352924-AI-Design-Generator',
        isBoolean: true,
        inlineCreate: true,
        dedicatedEditor: true,
        dataKey: 'aiDesignGenConfigured',
        displayMode: 'boolean'
    },
    {
        key: 'ai-lifestyle-scenes',
        name: T._('AI Lifestyle Scenes', 'Admin'),
        description: T._('Generate lifestyle product staging with AI', 'Admin'),
        route: '/ai-creative-tools/lifestyle-scenes-rules',
        permissions: ['AI_CREATIVE_TOOLS'],
        detailRoute: '/ai-creative-tools/lifestyle-scenes-builder',
        buildSetupRoute: () => '/ai-creative-tools/lifestyle-scenes-builder/',
        addPanelKey: 'aiStagingRule',
        buildEmbeddedUrl: (itemId) => `/ai-creative-tools/lifestyle-scenes-builder/${toLocalID(itemId)}?embedded=true`,
        environments: ['CUSTOMIZER'],
        category: 'ai',
        helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/24189993100444-AI-Product-Staging',
        isBoolean: true,
        isSingleRule: true,
        inlineCreate: true,
        fullModeOnlyInSidebar: true,
        dataKey: 'aiStagingRules',
        displayMode: 'items-with-status'
    },
    {
        key: 'ai-try-on',
        name: T._('AI Try-on', 'Admin'),
        description: T._('Virtual model try-on powered by AI', 'Admin'),
        route: '/ai-creative-tools/ai-tryon-rules',
        permissions: ['AI_CREATIVE_TOOLS'],
        detailRoute: '/ai-creative-tools/ai-tryon-builder',
        buildSetupRoute: () => '/ai-creative-tools/ai-tryon-builder/',
        addPanelKey: 'aiVirtualModelRule',
        buildEmbeddedUrl: (itemId) => `/ai-creative-tools/ai-tryon-builder/${toLocalID(itemId)}?embedded=true`,
        environments: ['CUSTOMIZER'],
        category: 'ai',
        helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/14416061856796-Virtual-Try-On',
        isBoolean: true,
        isSingleRule: true,
        inlineCreate: true,
        fullModeOnlyInSidebar: true,
        dataKey: 'aiVirtualModelRules',
        displayMode: 'items-with-status'
    },

    // Tools & Settings (alphabetical)
    {
        key: 'extension-fields',
        name: T._('Additional Custom Properties', 'Admin'),
        description: T._('Additional attributes not shown on the product', 'Admin'),
        route: '/settings/additional-properties',
        permissions: ['EXTENSION_FIELDS'],
        environments: ['COMPOSER'],
        category: 'tools-settings',
        helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360017548099-Dynamic-product-s-properties',
        isBoolean: true,
        externalSetupUrl: () => '/Composer?page=additional-properties',
        dataKey: 'extensionFields',
        displayMode: 'boolean'
    },
    {
        key: 'collage',
        name: T._('Collage layouts', 'Admin'),
        description: T._('Let customers upload images in collage grids', 'Admin'),
        route: '/tools/collage-layouts',
        permissions: ['COLLAGES'],
        detailRoute: '/tools/collage-layouts/rule',
        buildSetupRoute: () => '/tools/collage-layouts/rule/',
        addPanelKey: 'collageRule',
        buildEmbeddedUrl: (itemId, productId) =>
            itemId
                ? `/tools/collage-layouts/rule/${toLocalID(itemId)}?embedded=true`
                : `/tools/collage-layouts/rule/?embedded=true&fromProductId=${toLocalID(productId)}`,
        environments: ['CUSTOMIZER'],
        category: 'tools-settings',
        helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360010305840-Collage-Layouts-Tool',
        isBoolean: true,
        isSingleRule: true,
        fullModeOnly: true,
        inlineCreate: true,
        dataKey: 'collageRules',
        displayMode: 'items'
    },
    {
        key: 'area-background',
        name: T._('Fill Background', 'Admin'),
        description: T._('Let customers use colors and images to fill print areas', 'Admin'),
        route: '/tools/fill-background',
        permissions: ['CUSTOMIZER_PRODUCT_BACKGROUND'],
        detailRoute: '/tools/fill-background/rule',
        buildSetupRoute: () => '/tools/fill-background/rule/',
        addPanelKey: 'areaBackgroundRule',
        buildEmbeddedUrl: (itemId, productId) =>
            itemId
                ? `/tools/fill-background/rule/${toLocalID(itemId)}?embedded=true`
                : `/tools/fill-background/rule/?embedded=true&fromProductId=${toLocalID(productId)}`,
        environments: ['CUSTOMIZER'],
        category: 'tools-settings',
        helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360025012633-Product-background-editing-tool',
        isBoolean: true,
        isSingleRule: true,
        inlineCreate: true,
        dedicatedEditor: true,
        dataKey: 'areaBackgroundRules',
        displayMode: 'items'
    },
    {
        key: 'preset-configurations',
        name: T._('Pre-set configurations', 'Admin'),
        description: T._('Predefined compositions of attributes and options', 'Admin'),
        route: '/preset-configurations',
        permissions: ['PRE_SET_CONFIGURATIONS'],
        environments: ['COMPOSER'],
        category: 'tools-settings',
        helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/8786738011036-Pre-set-Configurations',
        externalSetupUrl: (pid) => `/Composer?id=${pid}&page=pre-set-configurations`,
        dataKey: 'presetConfigurations',
        displayMode: 'items'
    },
    {
        key: 'profanity',
        name: T._('Profanity check and image moderator', 'Admin'),
        description: T._('Block offensive words and moderate images', 'Admin'),
        route: '/tools/profanity-filter-menu',
        permissions: ['PROFANITY'],
        buildSetupRoute: () => '/tools/profanity-filter-rule',
        addPanelKey: 'profanityRule',
        buildEmbeddedUrl: (itemId) => `/tools/profanity-filter-rule?rule_id=${itemId}&embedded=true`,
        environments: ['CUSTOMIZER'],
        category: 'tools-settings',
        helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/4423286336284-Profanity-Filter-and-Image-Moderator',
        isBoolean: true,
        isSingleRule: true,
        inlineCreate: true,
        dedicatedEditor: true,
        dataKey: 'profanityRules',
        displayMode: 'items'
    },
    {
        key: 'shapes',
        name: T._('Shapes and masks', 'Admin'),
        description: T._('Shapes for decoration or image masking', 'Admin'),
        route: '/tools/shapes',
        permissions: ['SHAPES'],
        buildEmbeddedUrl: (_itemId, pid) => `/tools/shapes/my-gallery?embedded=true&fromProductId=${toLocalID(pid) ?? pid}`,
        environments: ['CUSTOMIZER'],
        category: 'tools-settings',
        helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360020531379-Shapes-and-Masks',
        isBoolean: true,
        fullModeOnlyInSidebar: true,
        dataKey: 'shapesConfigured',
        displayMode: 'boolean'
    }
];

export const TOOL_CATEGORY_ORDER: ToolCategory[] = ['product-setup', 'tools-settings', 'ai', 'orders-output'];

export const TOOL_EDITOR_CONFIGS: Record<string, { subtitle: string; fields: RuleField[] }> = {
    templates: {
        subtitle: T._('Pre-designed template', 'Admin'),
        fields: [
            {
                key: 'adaptToVariants',
                label: T._('Adapt items to variants', 'Admin'),
                type: 'switch',
                defaultValue: true,
                section: T._('General', 'Admin')
            },
            { key: 'isDefault', label: T._('Set as default template', 'Admin'), type: 'switch', defaultValue: false, section: T._('General', 'Admin') }
        ]
    },
    images: {
        subtitle: T._('Images and clipart', 'Admin'),
        fields: [
            { key: 'allowUpload', label: T._('Allow user image upload', 'Admin'), type: 'switch', defaultValue: true, section: T._('Upload', 'Admin') },
            { key: 'maxFileSize', label: T._('Max file size (MB)', 'Admin'), type: 'number', defaultValue: '10', section: T._('Upload', 'Admin') },
            { key: 'allowSvg', label: T._('Allow SVG upload', 'Admin'), type: 'switch', defaultValue: true, section: T._('Formats', 'Admin') },
            { key: 'allowPdf', label: T._('Allow PDF upload', 'Admin'), type: 'switch', defaultValue: false, section: T._('Formats', 'Admin') },
            {
                key: 'canChangeColors',
                label: T._('Allow SVG color change', 'Admin'),
                type: 'switch',
                defaultValue: true,
                section: T._('Editing', 'Admin')
            },
            { key: 'canUseFilters', label: T._('Allow image filters', 'Admin'), type: 'switch', defaultValue: true, section: T._('Editing', 'Admin') }
        ]
    },
    shapes: {
        subtitle: T._('Shape rule', 'Admin'),
        fields: [
            {
                key: 'applyToAll',
                label: T._('Apply to all print types', 'Admin'),
                type: 'switch',
                defaultValue: true,
                section: T._('Assignment', 'Admin')
            },
            { key: 'allowResize', label: T._('Allow shape resize', 'Admin'), type: 'switch', defaultValue: true, section: T._('Options', 'Admin') },
            { key: 'allowRotate', label: T._('Allow shape rotation', 'Admin'), type: 'switch', defaultValue: true, section: T._('Options', 'Admin') },
            { key: 'useMask', label: T._('Use as image mask', 'Admin'), type: 'switch', defaultValue: false, section: T._('Options', 'Admin') }
        ]
    },
    'area-background': {
        subtitle: T._('Background rule', 'Admin'),
        fields: [
            { key: 'allowColors', label: T._('Allow solid colors', 'Admin'), type: 'switch', defaultValue: true, section: T._('Options', 'Admin') },
            {
                key: 'allowImages',
                label: T._('Allow background images', 'Admin'),
                type: 'switch',
                defaultValue: true,
                section: T._('Options', 'Admin')
            },
            { key: 'allowUpload', label: T._('Allow image upload', 'Admin'), type: 'switch', defaultValue: false, section: T._('Options', 'Admin') },
            {
                key: 'applyToAll',
                label: T._('Apply to all print types', 'Admin'),
                type: 'switch',
                defaultValue: true,
                section: T._('Assignment', 'Admin')
            }
        ]
    },
    placement: {
        subtitle: T._('Placement rule', 'Admin'),
        fields: [
            {
                key: 'nestedDesigns',
                label: T._('Enable nested designs', 'Admin'),
                type: 'switch',
                defaultValue: false,
                section: T._('Features', 'Admin')
            },
            { key: 'qrCodes', label: T._('Enable QR codes', 'Admin'), type: 'switch', defaultValue: false, section: T._('Features', 'Admin') },
            { key: 'dynamicInfo', label: T._('Enable dynamic info', 'Admin'), type: 'switch', defaultValue: false, section: T._('Features', 'Admin') },
            { key: 'barcodes', label: T._('Enable barcodes', 'Admin'), type: 'switch', defaultValue: false, section: T._('Features', 'Admin') }
        ]
    },
    '3d-preview': {
        subtitle: T._('3D preview', 'Admin'),
        fields: [
            { key: 'enableAR', label: T._('Enable Augmented Reality', 'Admin'), type: 'switch', defaultValue: true, section: T._('Features', 'Admin') },
            { key: 'autoRotate', label: T._('Auto-rotate model', 'Admin'), type: 'switch', defaultValue: false, section: T._('Display', 'Admin') },
            { key: 'showEnvironment', label: T._('Show environment', 'Admin'), type: 'switch', defaultValue: true, section: T._('Display', 'Admin') }
        ]
    },
    profanity: {
        subtitle: T._('Profanity filter rule', 'Admin'),
        fields: [
            { key: 'blockText', label: T._('Block offensive text', 'Admin'), type: 'switch', defaultValue: true, section: T._('Text', 'Admin') },
            { key: 'blockImages', label: T._('Block offensive images', 'Admin'), type: 'switch', defaultValue: true, section: T._('Images', 'Admin') },
            {
                key: 'sensitivity',
                label: T._('Sensitivity', 'Admin'),
                type: 'select',
                defaultValue: 'MEDIUM',
                section: T._('General', 'Admin'),
                options: [
                    { value: 'LOW', label: T._('Low', 'Admin') },
                    { value: 'MEDIUM', label: T._('Medium', 'Admin') },
                    { value: 'HIGH', label: T._('High', 'Admin') }
                ]
            },
            {
                key: 'applyToAll',
                label: T._('Apply to all print types', 'Admin'),
                type: 'switch',
                defaultValue: true,
                section: T._('Assignment', 'Admin')
            }
        ]
    },
    'ai-image-generation': {
        subtitle: T._('AI Image Generator', 'Admin'),
        fields: [
            { key: 'enabled', label: T._('Enable AI image generation', 'Admin'), type: 'switch', defaultValue: true, section: T._('General', 'Admin') },
            {
                key: 'allowCustomPrompt',
                label: T._('Allow custom prompts', 'Admin'),
                type: 'switch',
                defaultValue: true,
                section: T._('Options', 'Admin')
            },
            {
                key: 'maxGenerations',
                label: T._('Max generations per session', 'Admin'),
                type: 'number',
                defaultValue: '10',
                section: T._('Limits', 'Admin')
            }
        ]
    },
    'ai-lifestyle-scenes': {
        subtitle: T._('AI Lifestyle Scene rule', 'Admin'),
        fields: [
            {
                key: 'allowCustomBackground',
                label: T._('Allow custom background', 'Admin'),
                type: 'switch',
                defaultValue: true,
                section: T._('General', 'Admin')
            },
            { key: 'numberOfImages', label: T._('Number of images', 'Admin'), type: 'number', defaultValue: '4', section: T._('General', 'Admin') },
            {
                key: 'applyToAll',
                label: T._('Apply to all products', 'Admin'),
                type: 'switch',
                defaultValue: false,
                section: T._('Assignment', 'Admin')
            }
        ]
    },
    'ai-try-on': {
        subtitle: T._('AI Try-on rule', 'Admin'),
        fields: [
            { key: 'autoFit', label: T._('Auto-fit to model', 'Admin'), type: 'switch', defaultValue: true, section: T._('General', 'Admin') },
            { key: 'allowPoseChange', label: T._('Allow pose change', 'Admin'), type: 'switch', defaultValue: true, section: T._('Options', 'Admin') },
            {
                key: 'applyToAll',
                label: T._('Apply to all products', 'Admin'),
                type: 'switch',
                defaultValue: false,
                section: T._('Assignment', 'Admin')
            }
        ]
    },
    moq: {
        subtitle: T._('MOQ rule', 'Admin'),
        fields: [
            { key: 'minimumQuantity', label: T._('Minimum order quantity', 'Admin'), type: 'number', defaultValue: '1' },
            {
                key: 'stepType',
                label: T._('Step type', 'Admin'),
                type: 'select',
                defaultValue: 'FIXED',
                options: [
                    { value: 'FIXED', label: T._('Fixed steps', 'Admin') },
                    { value: 'CUSTOM', label: T._('Custom tiers', 'Admin') }
                ]
            },
            {
                key: 'fixedStep',
                label: T._('Fixed step value', 'Admin'),
                type: 'number',
                defaultValue: '1',
                dependsOn: 'stepType:FIXED'
            },
            {
                key: 'customTiers',
                label: T._('Custom tiers (comma-separated)', 'Admin'),
                type: 'text',
                defaultValue: '',
                dependsOn: 'stepType:CUSTOM'
            }
        ]
    },
    'bulk-variations': {
        subtitle: T._('Bulk variation form', 'Admin'),
        fields: [
            { key: 'enabled', label: T._('Enable bulk variations', 'Admin'), type: 'switch', defaultValue: true, section: T._('General', 'Admin') },
            {
                key: 'allowMixVariants',
                label: T._('Allow mixed variants', 'Admin'),
                type: 'switch',
                defaultValue: true,
                section: T._('Options', 'Admin')
            }
        ]
    },
    'get-a-quote': {
        subtitle: T._('Quote rule', 'Admin'),
        fields: [
            { key: 'showButton', label: T._('Show quote button', 'Admin'), type: 'switch', defaultValue: true, section: T._('Display', 'Admin') },
            { key: 'requireEmail', label: T._('Require email', 'Admin'), type: 'switch', defaultValue: true, section: T._('Fields', 'Admin') },
            { key: 'requirePhone', label: T._('Require phone', 'Admin'), type: 'switch', defaultValue: false, section: T._('Fields', 'Admin') },
            { key: 'requireMessage', label: T._('Require message', 'Admin'), type: 'switch', defaultValue: false, section: T._('Fields', 'Admin') }
        ]
    }
};

export const PROFANITY_LANGS = [
    { code: 'en', label: T._('English', 'Admin') },
    { code: 'ar', label: T._('Arabic', 'Admin') },
    { code: 'fr', label: T._('French', 'Admin') },
    { code: 'de', label: T._('German', 'Admin') },
    { code: 'hi', label: T._('Hindi', 'Admin') },
    { code: 'ja', label: T._('Japanese', 'Admin') },
    { code: 'it', label: T._('Italian', 'Admin') },
    { code: 'pt', label: T._('Portuguese', 'Admin') },
    { code: 'ru', label: T._('Russian', 'Admin') },
    { code: 'es', label: T._('Spanish', 'Admin') },
    { code: 'th', label: T._('Thai', 'Admin') },
    { code: 'tr', label: T._('Turkish', 'Admin') }
];

export const PROFANITY_CATEGORIES = [
    {
        name: 'nudity',
        label: T._('Nudity', 'Admin'),
        subcategories: [
            { name: 'nudity', id: 1, defaultValue: 92 },
            { name: 'nuditypartial', id: 9, defaultValue: 95 },
            { name: 'nuditysafe', id: 10, defaultValue: 90 }
        ]
    },
    {
        name: 'wad',
        label: T._('WAD', 'Admin'),
        subcategories: [
            { name: 'weapon', id: 2, defaultValue: 90 },
            { name: 'alcohol', id: 11, defaultValue: 90 },
            { name: 'drugs', id: 12, defaultValue: 90 }
        ]
    },
    {
        name: 'offensive',
        label: T._('Hate and offensive', 'Admin'),
        subcategories: [{ name: 'offensive', id: 3, defaultValue: 90 }]
    },
    { name: 'celebrities', label: T._('Celebrities', 'Admin'), subcategories: [{ name: 'celebrities', id: 4, defaultValue: 92 }] },
    {
        name: 'text',
        label: T._('Images with texts', 'Admin'),
        subcategories: [
            { name: 'artificialtext', id: 5, defaultValue: 82 },
            { name: 'naturaltext', id: 16, defaultValue: 90 }
        ]
    },
    { name: 'ocr', label: T._('OCR', 'Admin'), subcategories: [{ name: 'ocr', id: 6, defaultValue: 1 }] },
    {
        name: 'faces',
        label: T._('Gender, minors, and faces', 'Admin'),
        subcategories: [
            { name: 'minor', id: 7, defaultValue: 94 },
            { name: 'female', id: 13, defaultValue: 92 },
            { name: 'male', id: 14, defaultValue: 92 },
            { name: 'sunglasses', id: 15, defaultValue: 95 }
        ]
    },
    { name: 'scam', label: T._('Scam', 'Admin'), subcategories: [{ name: 'scam', id: 8, defaultValue: 95 }] }
];

export const SUBCATEGORY_TO_CATEGORY: Record<string, string> = {
    nudity: 'nudity',
    nuditypartial: 'nudity',
    nuditysafe: 'nudity',
    weapon: 'wad',
    alcohol: 'wad',
    drugs: 'wad',
    offensive: 'offensive',
    celebrities: 'celebrities',
    artificialtext: 'text',
    naturaltext: 'text',
    ocr: 'ocr',
    minor: 'faces',
    female: 'faces',
    male: 'faces',
    sunglasses: 'faces',
    scam: 'scam'
};

export const CONDITION_OPERATORS = [
    { value: '=', label: T._('= Equal', 'Admin'), connector: T._('to', 'Admin') },
    { value: '!=', label: T._('\u2260 Not equal', 'Admin'), connector: T._('to', 'Admin') },
    { value: '<', label: T._('< Less', 'Admin'), connector: T._('than', 'Admin') },
    { value: '<=', label: T._('\u2264 Less/equal', 'Admin'), connector: T._('than', 'Admin') },
    { value: '>', label: T._('> Greater', 'Admin'), connector: T._('than', 'Admin') },
    { value: '>=', label: T._('\u2265 Greater/equal', 'Admin'), connector: T._('than', 'Admin') }
];

export const ACTION_TYPE_LABELS: Record<number, string> = {
    99: T._('Show objects', 'Admin'),
    2: T._('Hide objects', 'Admin'),
    3: T._('Set object material', 'Admin'),
    7: T._('Set sub object material', 'Admin'),
    4: T._('Set material color', 'Admin'),
    5: T._('Move camera', 'Admin'),
    11: T._('Set design material', 'Admin'),
    8: T._('Set camera pivot', 'Admin'),
    9: T._('Reset camera pivot', 'Admin'),
    17: T._('Set material parameter', 'Admin')
};

export const ACTION_TYPE_ORDER = [99, 2, 3, 7, 4, 5, 11, 8, 9, 17];
export const OBJECT_ONLY_ACTION_TYPES = new Set([99, 2]);

export const ACTION_TYPE_COLORS: Record<number, { bg: string; fg: string }> = {
    99: { bg: '#d1fae5', fg: '#065f46' },
    2: { bg: '#fee2e2', fg: '#991b1b' },
    3: { bg: '#ede9fe', fg: '#7c3aed' },
    7: { bg: '#ede9fe', fg: '#7c3aed' },
    4: { bg: '#dbeafe', fg: '#1e40af' },
    5: { bg: '#fef3c7', fg: '#92400e' },
    11: { bg: '#fce7f3', fg: '#be185d' },
    8: { bg: '#fef3c7', fg: '#92400e' },
    9: { bg: '#fef3c7', fg: '#92400e' },
    17: { bg: '#e0e7ff', fg: '#3730a3' }
};

export const MATERIAL_COLOR_TYPES = [
    { label: T._('Diffuse', 'Admin'), value: 'diffuseColor' },
    { label: T._('Albedo', 'Admin'), value: 'albedoColor' },
    { label: T._('Base', 'Admin'), value: 'baseColor' },
    { label: T._('Ambient', 'Admin'), value: 'ambientColor' },
    { label: T._('Specular', 'Admin'), value: 'specularColor' },
    { label: T._('Emissive', 'Admin'), value: 'emissiveColor' },
    { label: T._('Reflection', 'Admin'), value: 'reflectionColor' }
];

export const MATERIAL_SELECTOR_ACTION_TYPES = new Set([4, 17]);
export const MATERIAL_VALUE_ACTION_TYPES = new Set([3, 7, 11]);
export const CAMERA_ACTION_TYPES = new Set([5, 8]);
export const NO_FIELDS_ACTION_TYPES = new Set([9]);

// ========== Helpers ==========

export const getToolLabel = (env: string) => {
    switch (env) {
        case 'CUSTOMIZER':
            return T._('Customizer', 'Admin');
        case 'COMPOSER':
            return T._('Configurator', 'Admin');
        case 'VIEWER':
            return T._('Viewer', 'Admin');
        default:
            return env;
    }
};

export const getSalesChannelCount = (product: SidePanelProduct) => {
    let count = 0;
    if (product.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE') {
        count = 1;
    }
    count += product.additionalCodes.filter((ac) => ac.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE').length;
    return count;
};

export interface ProductSalesChannelInfo {
    salesChannelID: string;
    productCode: string;
    name: string;
    ecommerce: string;
    url: string;
    isOtherSalesChannel: boolean;
}

export const getAllSalesChannels = (product: SidePanelProduct): ProductSalesChannelInfo[] => {
    const channels: ProductSalesChannelInfo[] = [];

    const ids: string[] = [];
    if (product.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE') ids.push(product.salesChannel.id);
    product.additionalCodes.forEach((ac) => {
        if (ac.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE') ids.push(ac.salesChannel.id);
    });
    const storeNumbers = generateStoreNumbers(ids);
    const getStoreNumber = (id: string) => storeNumbers.find((x) => x.salesChannelID === id)?.storeNumber || 1;

    if (product.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE') {
        const ecom = product.salesChannel.ecommerceSettings?.ecommerceName || '';
        const customName = product.salesChannel.detail?.properties?.name || '';
        channels.push({
            salesChannelID: product.salesChannel.id,
            productCode: product.code,
            name: customName || `Store ${getStoreNumber(product.salesChannel.id)}`,
            ecommerce: salesChannelsLabels(ecom) || ecom,
            url: product.salesChannel.ecommerceSettings?.definitiveUrl || product.salesChannel.ecommerceSettings?.remoteUrl || '',
            isOtherSalesChannel: false
        });
    }
    product.additionalCodes.forEach((ac) => {
        if (ac.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE') {
            const ecom = ac.salesChannel.ecommerceSettings?.ecommerceName || '';
            const customName = ac.salesChannel.detail?.properties?.name || '';
            channels.push({
                salesChannelID: ac.salesChannel.id,
                productCode: ac.code,
                name: customName || `Store ${getStoreNumber(ac.salesChannel.id)}`,
                ecommerce: salesChannelsLabels(ecom) || ecom,
                url: ac.salesChannel.ecommerceSettings?.definitiveUrl || ac.salesChannel.ecommerceSettings?.remoteUrl || '',
                isOtherSalesChannel: true
            });
        }
    });
    return channels;
};

export const getToolInfo = (
    product: SidePanelProduct,
    tool: ToolDefinition,
    toolData: ProductToolData
): { count: number; status: string | null; items: { id: string; name: string; previewUrl?: string | null }[] } => {
    // Custom cases that read from product or have unique logic
    if (tool.key === 'printing-methods') {
        const nonDefaultMethods = (product.printTypes || []).filter((pt) => pt.printTypeID?.toString() !== '1');
        return {
            count: nonDefaultMethods.length,
            status: null,
            items: nonDefaultMethods.map((pt) => ({ id: toGlobalID(pt.printTypeID, 'PrintType'), name: pt.name }))
        };
    }
    if (tool.key === 'images') {
        const restrictions = toolData.imageRestrictions || [];
        const anyEnabled = restrictions.some((r) => r.clipartGalleries);
        const hasContent = !!toolData.hasGalleryContent;
        const configured = anyEnabled && hasContent;
        return {
            count: configured || hasContent ? 1 : 0,
            status: toolData.loaded && configured ? T._('Configured', 'Admin') : null,
            items: []
        };
    }

    if (tool.key === 'shapes') {
        const assigned = !!toolData.shapesConfigured;
        const hasShapes = !!toolData.hasShapes;
        if (assigned) {
            return { count: 1, status: toolData.loaded ? T._('Configured', 'Admin') : null, items: [] };
        }
        if (hasShapes) {
            return { count: 0, status: toolData.loaded ? T._('Configured', 'Admin') : null, items: [] };
        }
        return { count: 0, status: null, items: [] };
    }

    if (tool.key === 'placement') {
        const placements = toolData.placements || [];
        return {
            count: placements.length,
            status: null,
            items: placements.map((p) => ({ id: p.id, name: p.name || `#${p.id}` }))
        };
    }

    if (tool.key === 'bulk-variations') {
        const flag = !!toolData.bulkVariationEnabled;
        return {
            count: flag ? 1 : 0,
            status: toolData.loaded ? (flag ? T._('Enabled', 'Admin') : null) : null,
            items: []
        };
    }

    // Generic handling via tool definition properties
    if (!tool.dataKey) return { count: 0, status: null, items: [] };

    const rawData = toolData[tool.dataKey];

    switch (tool.displayMode) {
        case 'items': {
            const arr = (Array.isArray(rawData) ? rawData : []) as { id: string; name: string; previewUrl?: string | null }[];
            return { count: arr.length, status: null, items: arr.map((item) => ({ ...item, name: item.name || `#${item.id}` })) };
        }
        case 'items-with-status': {
            const arr = (Array.isArray(rawData) ? rawData : []) as { id: string; name: string }[];
            return {
                count: arr.length,
                status: toolData.loaded && arr.length ? `${arr.length} ${arr.length === 1 ? T._('rule', 'Admin') : T._('rules', 'Admin')}` : null,
                items: arr
            };
        }
        case 'boolean': {
            const flag = Array.isArray(rawData) ? rawData.length > 0 : !!rawData;
            const enableLabel =
                tool.key === 'ai-image-generation' || tool.key === 'ai-design-generation' ? T._('Enabled', 'Admin') : T._('Configured', 'Admin');
            return {
                count: flag ? 1 : 0,
                status: toolData.loaded ? (flag ? enableLabel : null) : null,
                items: []
            };
        }
        case 'single-item': {
            const item = rawData as { id: string; name: string } | null | undefined;
            return { count: item ? 1 : 0, status: null, items: item ? [item] : [] };
        }
        default:
            return { count: 0, status: null, items: [] };
    }
};

export const parseFormula = (formula: string): { operator: string; values: string[] } => {
    try {
        const parsed = JSON.parse(formula);
        return { operator: parsed.operator || '=', values: parsed.values || [] };
    } catch {
        return { operator: '=', values: [] };
    }
};

export const buildFormula = (operator: string, values: string[]): string => {
    return JSON.stringify({ operator, values });
};
